import { HelpCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface HelpTooltipProps {
  content: string;
  className?: string;
  side?: "top" | "right" | "bottom" | "left";
  size?: "sm" | "md" | "lg";
}

export const HelpTooltip = ({ 
  content, 
  className,
  side = "top",
  size = "sm"
}: HelpTooltipProps) => {
  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6"
  };

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <button 
            type="button"
            className={cn(
              "inline-flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors cursor-help",
              className
            )}
          >
            <HelpCircle className={sizeClasses[size]} />
          </button>
        </TooltipTrigger>
        <TooltipContent side={side} className="max-w-xs text-sm">
          <p>{content}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Specific help content for Nest Points system
export const NestPointsHelp = {
  // Policy tab
  policyName: "Le nom de votre politique Nest Points, visible par les employés.",
  attributionModel: "Choisissez comment les points sont distribués : uniformément à tous ou selon le niveau de séniorité.",
  basePointsPerWeek: "Le coût de base en points pour une semaine de workation dans le même fuseau horaire.",
  maximumBalance: "Le solde maximum de Nest Points qu'un employé peut accumuler.",
  monthlyPointsCredit: "Le nombre de points crédités automatiquement chaque mois à tous les employés.",
  
  // Seniority levels
  seniorityLevel: "Définissez des niveaux de séniorité (L2-L5) pour attribuer des points mensuels différenciés.",
  seniorityExample: "Exemple : L2 = 5 pts/mois, L3 = 10 pts/mois, L4 = 15 pts/mois, L5 = 20 pts/mois",
  
  // Timezone/Distance
  timezoneRestriction: "Limitez les destinations accessibles selon le décalage horaire avec le siège.",
  distancePricing: "Appliquez un multiplicateur de points selon la distance (fuseau horaire) de la destination.",
  distanceExample: "Exemple : même fuseau = x1, ±2h = x1.5, ±4h = x2, au-delà = x2.5",
  headquartersTimezone: "Le fuseau horaire de référence pour calculer les distances des destinations.",
  
  // Employee points
  bonusPoints: "Ajoutez des points bonus à un employé pour récompenser des performances exceptionnelles.",
  currentBalance: "Le solde actuel de l'employé, incluant les crédits mensuels et bonus.",
  
  // Calculator
  workationCalculator: "Simulez le coût en points d'une workation selon la destination et la durée.",
  
  // Employee guide
  howPointsWork: "Les Nest Points sont crédités mensuellement et vous permettent de financer vos workations.",
  planningBudget: "Consultez votre solde et le coût des destinations pour planifier votre prochain voyage.",
  pointCosts: "Le coût varie selon la distance : plus la destination est éloignée, plus elle coûte de points.",
};
